import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { MessageSquareWarning, FileWarning, Lightbulb, AlertTriangle, Clock, CheckCircle, Eye } from 'lucide-react';

const typeIcons = { feedback: MessageSquareWarning, incident: FileWarning, improvement: Lightbulb, urgent: AlertTriangle };
const typeColors = { feedback: 'gold', incident: 'red-400', improvement: 'emerald-400', urgent: 'amber-400' };
const priorityBadge = { low: 'bg-zinc-500/20 text-zinc-400', medium: 'bg-blue-500/20 text-blue-400', high: 'bg-amber-500/20 text-amber-400', urgent: 'bg-red-500/20 text-red-400' };
const statusBadge = { new: 'bg-blue-500/20 text-blue-400', reviewed: 'bg-amber-500/20 text-amber-400', resolved: 'bg-emerald-500/20 text-emerald-400', dismissed: 'bg-zinc-500/20 text-zinc-400' };

export default function ReportsPage() {
  const [reports, setReports] = useState([]);
  const [filter, setFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewReport, setViewReport] = useState(null);
  const [response, setResponse] = useState('');
  const [updating, setUpdating] = useState(false);

  const fetchReports = useCallback(async () => {
    try {
      let url = '/quick-reports';
      const params = [];
      if (filter !== 'all') params.push(`report_type=${filter}`);
      if (statusFilter !== 'all') params.push(`status=${statusFilter}`);
      if (params.length) url += '?' + params.join('&');
      const { data } = await api.get(url);
      setReports(data);
    } catch (err) { console.error('[ReportsPage.js] error:', err); }
  }, [filter, statusFilter]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const updateStatus = async (reportId, newStatus) => {
    setUpdating(true);
    try {
      await api.put(`/quick-reports/${reportId}/status`, { status: newStatus, response });
      toast.success(`Report marked as ${newStatus}`);
      setViewReport(null);
      setResponse('');
      fetchReports();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setUpdating(false); }
  };

  const openDetail = (report) => { setViewReport(report); setResponse(report.response || ''); };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const counts = { all: reports.length, new: reports.filter(r => r.status === 'new').length, urgent: reports.filter(r => r.priority === 'urgent').length };

  return (
    <div className="animate-fadeIn">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex gap-1.5">
          {['all', 'feedback', 'incident', 'improvement', 'urgent'].map(t => (
            <Button key={t} size="sm" variant={filter === t ? 'default' : 'ghost'} onClick={() => setFilter(t)}
              className={`h-7 rounded-sm font-sans text-xs capitalize ${filter === t ? 'bg-gold/10 text-gold border border-gold/20' : 'text-zinc-500 hover:text-zinc-300'}`}
              data-testid={`filter-${t}`}>
              {t === 'all' ? 'All' : t}
            </Button>
          ))}
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-32 h-7 bg-transparent border border-white/10 text-white font-sans text-xs" data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-bg-secondary border border-white/10">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="reviewed">Reviewed</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="dismissed">Dismissed</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex gap-2 ml-auto text-xs font-sans">
          <span className="text-zinc-500">{counts.all} total</span>
          {counts.new > 0 && <span className="text-blue-400">{counts.new} new</span>}
          {counts.urgent > 0 && <span className="text-red-400">{counts.urgent} urgent</span>}
        </div>
      </div>

      {reports.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16">
          <CheckCircle className="w-10 h-10 text-zinc-700 mb-3" />
          <p className="text-zinc-500 font-sans" data-testid="no-reports">No reports found</p>
        </div>
      ) : (
        <div className="space-y-2">
          {reports.map(report => {
            const Icon = typeIcons[report.report_type] || MessageSquareWarning;
            const color = typeColors[report.report_type] || 'gold';
            return (
              <div key={report.id}
                onClick={() => openDetail(report)}
                className={`p-4 bg-white/[0.03] border rounded-sm card-hover cursor-pointer ${report.priority === 'urgent' ? 'border-red-500/30' : 'border-white/5'}`}
                data-testid={`report-${report.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-sm bg-${color}/10 flex items-center justify-center flex-shrink-0 mt-0.5`}>
                    <Icon className={`w-4 h-4 text-${color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-white font-sans text-sm font-medium">{report.subject || report.type_label}</span>
                      <Badge className={`${priorityBadge[report.priority]} border-0 font-sans text-[10px]`}>{report.priority}</Badge>
                      <Badge className={`${statusBadge[report.status]} border-0 font-sans text-[10px]`}>{report.status}</Badge>
                    </div>
                    <p className="text-zinc-500 font-sans text-xs line-clamp-1 mb-1">{report.description}</p>
                    <div className="flex items-center gap-3 text-[10px] text-zinc-600 font-sans">
                      <span>{report.submitted_by_name} ({report.submitted_by_role})</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(report.created_at)}</span>
                    </div>
                  </div>
                  <Eye className="w-4 h-4 text-zinc-600 flex-shrink-0" />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!viewReport} onOpenChange={v => { if (!v) setViewReport(null); }}>
        <DialogContent aria-describedby={undefined} className="bg-bg-secondary border border-white/10 max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto">
          {viewReport && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-white flex items-center gap-2">
                  {React.createElement(typeIcons[viewReport.report_type] || MessageSquareWarning, { className: `w-5 h-5 text-${typeColors[viewReport.report_type]}` })}
                  {viewReport.type_label}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="flex gap-2 flex-wrap">
                  <Badge className={`${priorityBadge[viewReport.priority]} border-0 font-sans text-xs`}>{viewReport.priority}</Badge>
                  <Badge className={`${statusBadge[viewReport.status]} border-0 font-sans text-xs`}>{viewReport.status}</Badge>
                </div>
                {viewReport.subject && (
                  <div><p className="text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-sans mb-1">Subject</p><p className="text-white font-sans text-sm">{viewReport.subject}</p></div>
                )}
                <div><p className="text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-sans mb-1">Description</p><p className="text-zinc-300 font-sans text-sm whitespace-pre-wrap">{viewReport.description}</p></div>
                {Object.keys(viewReport.extra_fields || {}).length > 0 && (
                  <div className="space-y-2">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-sans">Additional Details</p>
                    {Object.entries(viewReport.extra_fields).map(([k, v]) => v && (
                      <div key={k} className="flex justify-between text-sm font-sans">
                        <span className="text-zinc-500 capitalize">{k.replace(/_/g, ' ')}</span>
                        <span className="text-zinc-300">{v}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="pt-2 border-t border-white/5 text-xs font-sans text-zinc-500">
                  <p>Submitted by: {viewReport.submitted_by_name} ({viewReport.submitted_by_role})</p>
                  <p>Date: {formatDate(viewReport.created_at)}</p>
                  {viewReport.reviewed_by_name && <p className="mt-1">Reviewed by: {viewReport.reviewed_by_name} on {formatDate(viewReport.reviewed_at)}</p>}
                </div>
                <div className="space-y-2 pt-2 border-t border-white/5">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-sans">Management Response</p>
                  <textarea
                    value={response}
                    onChange={e => setResponse(e.target.value)}
                    className="w-full bg-transparent border border-white/10 text-white font-sans text-sm rounded-sm p-3 min-h-[80px] focus:border-gold/50 focus:outline-none resize-none"
                    placeholder="Add a response..."
                    data-testid="report-response-input"
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => updateStatus(viewReport.id, 'reviewed')} disabled={updating} className="h-8 bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 rounded-sm font-sans text-xs" data-testid="mark-reviewed">Reviewed</Button>
                    <Button size="sm" onClick={() => updateStatus(viewReport.id, 'resolved')} disabled={updating} className="h-8 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 rounded-sm font-sans text-xs" data-testid="mark-resolved">Resolved</Button>
                    <Button size="sm" onClick={() => updateStatus(viewReport.id, 'dismissed')} disabled={updating} className="h-8 bg-zinc-500/10 text-zinc-400 border border-zinc-500/20 hover:bg-zinc-500/20 rounded-sm font-sans text-xs" data-testid="mark-dismissed">Dismiss</Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
